import { NavLink, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard,
  User,
  MessageSquare,
  ShieldCheck,
  Star,
  LogOut,
  Menu,
  X,
  FileText,
  Database,
  ExternalLink,
} from 'lucide-react';
import { useState } from 'react';
import { useBranding } from '../contexts/BrandingContext';
import { useCmsNavGroups } from '../contexts/CmsNavContext';
import { useCmsLayout } from '../contexts/CmsLayoutContext';
import { clearAuth, getRefreshToken } from '../lib/auth';
import { api } from '../lib/api';
import type { CmsNavItem } from '../contexts/CmsNavContext';

const CONTENT_WIDTH_CLASS: Record<string, string> = {
  narrow: 'max-w-2xl',
  normal: 'max-w-5xl',
  wide: 'max-w-7xl',
  full: 'max-w-none',
};

const STATIC_NAV_ITEMS = [
  { to: '/dashboard', label: 'Dashboard', icon: LayoutDashboard },
  { to: '/profile', label: 'Profile', icon: User },
  { to: '/messages', label: 'Messages', icon: MessageSquare },
  { to: '/loyalty', label: 'Loyalty', icon: Star },
  { to: '/privacy', label: 'Privacy', icon: ShieldCheck },
];

function NavItemIcon({ type }: { type: string }) {
  if (type === 'collection') return <Database size={16} />;
  if (type === 'external') return <ExternalLink size={16} />;
  return <FileText size={16} />;
}

export function AppLayout({ children }: { children: React.ReactNode }) {
  const branding = useBranding();
  const navGroups = useCmsNavGroups();
  const layout = useCmsLayout();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);

  const contentWidthClass = CONTENT_WIDTH_CLASS[layout.contentWidth ?? 'normal'] ?? 'max-w-5xl';
  const headerPositionClass = layout.headerStyle === 'static' ? '' : 'sticky top-0';
  const isSidebar = layout.navPosition === 'sidebar-left';

  const handleLogout = async () => {
    try {
      const rt = getRefreshToken();
      if (rt) await api.post('/customer/auth/logout', { refreshToken: rt });
    } catch {
      /* ignore */
    }
    clearAuth();
    navigate('/login', { replace: true });
  };

  const navLinkClass = ({ isActive }: { isActive: boolean }) =>
    [
      'flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm font-medium transition-colors',
      isActive
        ? 'bg-[--color-primary] text-white'
        : 'text-zinc-600 hover:bg-zinc-100 hover:text-zinc-900',
    ].join(' ');

  function renderNavItem(item: CmsNavItem) {
    if (item.isExternal) {
      // Guard against non-http(s) URLs (defence-in-depth; backend validates too)
      const safeHref =
        item.path.startsWith('http://') || item.path.startsWith('https://') ? item.path : '#';
      return (
        <a
          key={item.id}
          href={safeHref}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm font-medium transition-colors text-zinc-600 hover:bg-zinc-100 hover:text-zinc-900"
          onClick={() => setMobileOpen(false)}
        >
          <NavItemIcon type={item.type} />
          {item.label}
        </a>
      );
    }
    return (
      <NavLink
        key={item.id}
        to={item.path}
        className={navLinkClass}
        onClick={() => setMobileOpen(false)}
      >
        <NavItemIcon type={item.type} />
        {item.label}
      </NavLink>
    );
  }

  const NavItems = () => (
    <>
      {/* Static items — always shown */}
      {STATIC_NAV_ITEMS.map(({ to, label, icon: Icon }) => (
        <NavLink key={to} to={to} className={navLinkClass} onClick={() => setMobileOpen(false)}>
          <Icon size={16} />
          {label}
        </NavLink>
      ))}

      {/* Dynamic CMS nav items — grouped.
          Skip empty groups; place divider before the first group that has items. */}
      {navGroups.map((group, gi) => {
        if (group.items.length === 0) return null;
        const isFirstWithItems = navGroups.findIndex((g) => g.items.length > 0) === gi;
        return (
          <div key={group.label ?? `__group_${gi}`}>
            {isFirstWithItems && <div className="my-1.5 border-t border-zinc-100" />}
            {group.label && (
              <div className="px-3 pt-2 pb-1">
                <span className="text-[10px] font-semibold uppercase tracking-widest text-zinc-400">
                  {group.label}
                </span>
              </div>
            )}
            {group.items.map((item) => renderNavItem(item))}
          </div>
        );
      })}
    </>
  );

  const BrandLogo = () => (
    <div className="flex items-center gap-2.5">
      {branding.logoUrl ? (
        <img src={branding.logoUrl} alt={branding.name} className="h-7 w-auto object-contain" />
      ) : (
        <div
          className="w-7 h-7 rounded-lg flex items-center justify-center text-white text-xs font-bold"
          style={{ backgroundColor: 'var(--color-primary)' }}
        >
          {branding.name.charAt(0).toUpperCase()}
        </div>
      )}
      <span className="text-sm font-semibold text-zinc-900">{branding.name}</span>
    </div>
  );

  const MobileDrawer = () =>
    mobileOpen ? (
      <div className="sm:hidden bg-white border-b border-zinc-100 px-4 py-3 space-y-1 z-10">
        <NavItems />
        <div className="pt-1 border-t border-zinc-100">
          <button
            onClick={handleLogout}
            className="flex items-center gap-2.5 px-3 py-2 w-full text-left rounded-lg text-sm font-medium text-zinc-600 hover:bg-zinc-100 hover:text-zinc-900 transition-colors"
          >
            <LogOut size={16} />
            Sign out
          </button>
        </div>
      </div>
    ) : null;

  const Footer = () => {
    if (layout.footerStyle === 'minimal') {
      return (
        <footer className="border-t border-zinc-100 py-4 text-center text-xs text-zinc-400">
          {branding.name} &copy; {new Date().getFullYear()}
        </footer>
      );
    }
    if (layout.footerStyle === 'full') {
      return (
        <footer className="border-t border-zinc-100 bg-white">
          <div className={`${contentWidthClass} mx-auto px-4 sm:px-6 py-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4`}>
            <BrandLogo />
            <p className="text-xs text-zinc-400">&copy; {new Date().getFullYear()} {branding.name}. All rights reserved.</p>
          </div>
        </footer>
      );
    }
    return null;
  };

  if (isSidebar) {
    return (
      <div className="min-h-screen bg-zinc-50 flex flex-row">
        {/* ── Sidebar (desktop only) ─────────────────────────────────────── */}
        <aside className="hidden sm:flex w-56 flex-shrink-0 bg-white border-r border-zinc-100 flex-col">
          <div className="px-4 py-4 border-b border-zinc-100">
            <BrandLogo />
          </div>
          <nav className="flex-1 px-3 py-3 space-y-0.5 overflow-y-auto">
            <NavItems />
          </nav>
          <div className="px-3 py-3 border-t border-zinc-100">
            <button
              onClick={handleLogout}
              className="flex items-center gap-2.5 px-3 py-2 w-full text-left rounded-lg text-sm font-medium text-zinc-600 hover:bg-zinc-100 hover:text-zinc-900 transition-colors"
            >
              <LogOut size={16} />
              Sign out
            </button>
          </div>
        </aside>

        {/* ── Main area ──────────────────────────────────────────────────── */}
        <div className="flex-1 flex flex-col min-w-0">
          {/* Mobile header (hamburger only — sidebar hidden on mobile) */}
          <header className={`bg-white border-b border-zinc-100 z-20 ${headerPositionClass}`}>
            <div className="px-4 h-14 flex items-center justify-between sm:hidden">
              <BrandLogo />
              <button
                className="p-1.5 text-zinc-500 hover:text-zinc-900"
                onClick={() => setMobileOpen((v) => !v)}
                aria-label="Toggle menu"
              >
                {mobileOpen ? <X size={20} /> : <Menu size={20} />}
              </button>
            </div>
          </header>

          {/* Mobile drawer */}
          <MobileDrawer />

          <main className="flex-1 w-full px-4 sm:px-6 py-8">{children}</main>
          <Footer />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-50 flex flex-col">
      {/* ── Top header ─────────────────────────────────────────────────────── */}
      <header className={`bg-white border-b border-zinc-100 z-20 ${headerPositionClass}`}>
        <div className={`${contentWidthClass} mx-auto px-4 sm:px-6 h-14 flex items-center justify-between`}>
          <BrandLogo />

          {/* Desktop nav (hidden if minimal header style) */}
          {layout.headerStyle !== 'minimal' && (
            <nav className="hidden sm:flex items-center gap-1">
              <NavItems />
            </nav>
          )}

          {/* Right actions */}
          <div className="flex items-center gap-2">
            <button
              onClick={handleLogout}
              className="hidden sm:flex items-center gap-1.5 text-sm text-zinc-500 hover:text-zinc-900 transition-colors"
            >
              <LogOut size={15} />
              Sign out
            </button>
            {/* Mobile hamburger */}
            <button
              className="sm:hidden p-1.5 text-zinc-500 hover:text-zinc-900"
              onClick={() => setMobileOpen((v) => !v)}
              aria-label="Toggle menu"
            >
              {mobileOpen ? <X size={20} /> : <Menu size={20} />}
            </button>
          </div>
        </div>
      </header>

      {/* ── Mobile nav drawer ──────────────────────────────────────────────── */}
      <MobileDrawer />

      {/* ── Page content ───────────────────────────────────────────────────── */}
      <main className={`flex-1 ${contentWidthClass} mx-auto w-full px-4 sm:px-6 py-8`}>{children}</main>

      <Footer />
    </div>
  );
}
